import _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import {
    AGENT_GLOBAL_MODEL,
    AGENT_HARDWARE_3K_MODEL,
    AGENT_HARDWARE_EDGE_SENSOR_MODEL,
    AGENT_OS_NAME_FIELD,
    AGENT_OS_WINDOWS,
    AGENT_STATUS_FIELD,
    AGENT_STATUS_RUNNING,
    AGENT_TYPE_GLOBAL,
    AGENT_TYPE_VBCS,
    AGENT_TYPE_VIRTUAL,
    AGENT_VITALS_TX_BUDGET_USED,
} from './AgentConstants';
import {
    SERVICE_TEST_INTERFACE_LOOKUP,
    SERVICE_TEST_INTERFACE_TOOLTIP_LOOKUP,
    SERVICE_TEST_INTERFACES,
} from '../general/Constants';

export function isWindows(agent) {
    return new RegExp(AGENT_OS_WINDOWS, 'i').test(_.get(agent, AGENT_OS_NAME_FIELD));
}

export function isHardware(agent) {
    return (agent.type !== AGENT_TYPE_GLOBAL && agent.type !== AGENT_TYPE_VIRTUAL) || agent.type === AGENT_TYPE_VBCS;
}

export function canCollectLogs(agent) {
    return agent.type !== AGENT_TYPE_GLOBAL && agent.status === AGENT_STATUS_RUNNING;
}

export function isHardware3k(agent) {
    if (!agent) {
        return false;
    }

    if (isHardware(agent)) {
        return _.get(agent, 'modelShort') === AGENT_HARDWARE_3K_MODEL;
    }

    return false;
}

export function hasWireless(agent) {
    if (!agent) {
        return false;
    }

    if (isHardware(agent)) {
        return _.get(agent, 'hasWireless') === true || isHardware3k(agent);
    }

    return false;
}

export function isHardwareEdgeSensor(agent) {
    if (!agent) {
        return false;
    }

    return _.get(agent, 'modelShort', false) === AGENT_HARDWARE_EDGE_SENSOR_MODEL;
}

export function isGlobal(agent) {
    return _.get(agent, 'modelShort') === AGENT_GLOBAL_MODEL;
}

export function isRunning(agent) {
    if (_.get(agent, AGENT_STATUS_FIELD) === AGENT_STATUS_RUNNING) {
        return true;
    }
    return false;
}

export function hasTxBudget(agent) {
    return _.has(agent, AGENT_VITALS_TX_BUDGET_USED);
}

export function isNextGeneration(agent) {
    if (!agent) return false;

    if (isHardware(agent)) {
        return _.get(agent, 'generation', -1) > 2 || isHardware3k(agent);
    }

    return false;
}

/**
 * Check if the agent is an Edge Sensor (hardware generation 4 or higher).
 * Used to filter agents for wifiConnectMultiTest.
 * Ported from portal/ui agents.service.js isEdgeSensor().
 *
 * @param {object} agent
 * @returns {boolean}
 */
export function isEdgeSensor(agent) {
    if (!agent) return false;
    if (isHardware(agent)) {
        return _.get(agent, 'generation', -1) > 3;
    }
    return false;
}

/**
 * Check if the agent supports dynamic WiFi connections.
 * Required for wifiConnect, wiFiMultiBssidConnection, and wifiConnectMultiTest types.
 * Ported from portal/ui agents.service.js isAgentDynamicWiFiEnabled().
 *
 * @param {object} agent
 * @returns {boolean}
 */
export function isAgentDynamicWiFiEnabled(agent) {
    return isNextGeneration(agent) && agent.isDynamicWirelessEnabled === true;
}

export function getAgentVoIPRegistrationInterfaceTooltip(agent) {
    const adapterType = _.get(agent, 'voipConfiguration.adapterType');
    const tooltip =
        SERVICE_TEST_INTERFACE_TOOLTIP_LOOKUP[adapterType] ||
        SERVICE_TEST_INTERFACE_TOOLTIP_LOOKUP[SERVICE_TEST_INTERFACES.DEFAULT];

    return i18n._(t`${tooltip}`);
}

export function getAgentVoIPRegistrationInterface(agent) {
    const adapterType = _.get(agent, 'voipConfiguration.adapterType');
    const testInterface =
        SERVICE_TEST_INTERFACE_LOOKUP[adapterType] || SERVICE_TEST_INTERFACE_LOOKUP[SERVICE_TEST_INTERFACES.DEFAULT];

    return i18n._(t`${testInterface}`);
}

export function isVoIPAgentDirectCallMode(agent) {
    const useDirectCalling = _.get(agent, 'voipConfiguration.useDirectCalling');

    return useDirectCalling;
}
